/**
 * editors.jsx
 * All per-element-type property editors.
 * Consolidated into one file because the visual-edits Babel plugin's
 * cross-file prop-source analysis breaks when editors are split across
 * many small files importing shared primitives.
 */
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Trash2 } from "lucide-react";
import {
  FONT_OPTIONS,
  ELEMENT_TYPES,
  COLUMN_ELEMENT_TYPES,
  getPageSize,
} from "@/lib/elements";
import {
  PropGroup,
  PropRow,
  PropInput,
  PropSelect,
  PropAlign,
  PropColor,
  PropTextarea,
  EjsBindingInput,
} from "./primitives";
import { FieldsEditor, SlotInnerBorderEditor } from "./shared";
import {
  ColumnsEditor,
  ColumnGroupsEditor,
  SubRowsEditor,
  TotalsRowsEditor,
} from "./tableHelpers";
import { RichTextEditor } from "./RichTextEditor";
import { useCustomElementsList, usePageSettings } from "./contexts";

/* ─────────────────────────── TEXT ELEMENTS ─────────────────────────── */
export function HeadingEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Content">
        <PropTextarea label="Text" value={props.content} onChange={(v) => onUpdate({ content: v })} rows={2} />
        <EjsBindingInput value={props.ejsBinding} onChange={(v) => onUpdate({ ejsBinding: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Typography">
        <PropInput  label="Size"      type="number" value={props.fontSize}      onChange={(v) => onUpdate({ fontSize: v })}      min={8} max={72} />
        <PropSelect label="Weight"    value={props.fontWeight}                  onChange={(v) => onUpdate({ fontWeight: v })}    options={["normal", "bold"]} />
        <PropSelect label="Font"      value={props.fontFamily}                  onChange={(v) => onUpdate({ fontFamily: v })}    options={FONT_OPTIONS} />
        <PropAlign  label="Align"     value={props.textAlign}                   onChange={(v) => onUpdate({ textAlign: v })} />
        <PropColor  label="Color"     value={props.color}                       onChange={(v) => onUpdate({ color: v })} />
        <PropInput  label="Spacing"   type="number" value={props.letterSpacing} onChange={(v) => onUpdate({ letterSpacing: v })} min={0} max={20} />
        <PropSelect label="Transform" value={props.textTransform}               onChange={(v) => onUpdate({ textTransform: v })} options={["none", "uppercase", "lowercase", "capitalize"]} />
      </PropGroup>
    </>
  );
}

export function TextEditor({ props, onUpdate }) {
  const useRich = props.richText === true;
  return (
    <>
      <PropGroup title="Content">
        <PropRow label="Rich Text">
          <Switch
            checked={useRich}
            onCheckedChange={(v) => onUpdate({ richText: v })}
            data-testid="text-richtext-toggle"
          />
        </PropRow>
        {useRich ? (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Rich Content</Label>
            <RichTextEditor
              value={props.richContent || `<p>${(props.content || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`}
              onChange={(html) => onUpdate({ richContent: html })}
            />
            <p className="text-[9px] text-gray-400">EJS variables can be inserted via the toolbar dropdown.</p>
          </div>
        ) : (
          <>
            <PropTextarea label="Text" value={props.content} onChange={(v) => onUpdate({ content: v })} rows={3} />
            <EjsBindingInput value={props.ejsBinding} onChange={(v) => onUpdate({ ejsBinding: v })} />
          </>
        )}
      </PropGroup>
      {!useRich && (
        <>
          <Separator className="my-3" />
          <PropGroup title="Typography">
            <PropInput  label="Size"    type="number" value={props.fontSize}   onChange={(v) => onUpdate({ fontSize: v })}   min={6} max={72} />
            <PropSelect label="Weight"  value={props.fontWeight}               onChange={(v) => onUpdate({ fontWeight: v })} options={["normal", "bold"]} />
            <PropSelect label="Style"   value={props.fontStyle}                onChange={(v) => onUpdate({ fontStyle: v })}  options={["normal", "italic"]} />
            <PropSelect label="Font"    value={props.fontFamily}               onChange={(v) => onUpdate({ fontFamily: v })} options={FONT_OPTIONS} />
            <PropAlign  label="Align"   value={props.textAlign}                onChange={(v) => onUpdate({ textAlign: v })} />
            <PropColor  label="Color"   value={props.color}                    onChange={(v) => onUpdate({ color: v })} />
            <PropInput  label="Line H." type="number" value={props.lineHeight} onChange={(v) => onUpdate({ lineHeight: v })} min={0.8} max={3} step={0.1} />
          </PropGroup>
        </>
      )}
    </>
  );
}

export function FooterEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Content">
        <PropTextarea label="Text" value={props.content} onChange={(v) => onUpdate({ content: v })} rows={2} />
        <EjsBindingInput value={props.ejsBinding} onChange={(v) => onUpdate({ ejsBinding: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size" type="number" value={props.fontSize} onChange={(v) => onUpdate({ fontSize: v })} />
        <PropAlign label="Align"     value={props.textAlign}              onChange={(v) => onUpdate({ textAlign: v })} />
        <PropColor label="Color"     value={props.color}                  onChange={(v) => onUpdate({ color: v })} />
      </PropGroup>
    </>
  );
}

export function PaymentTermsEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Content">
        <PropTextarea label="Terms" value={props.content} onChange={(v) => onUpdate({ content: v })} rows={4} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size" type="number" value={props.fontSize} onChange={(v) => onUpdate({ fontSize: v })} />
        <PropColor label="Color"     value={props.color}                  onChange={(v) => onUpdate({ color: v })} />
      </PropGroup>
    </>
  );
}

/* ─────────────────────────── MEDIA ELEMENTS ─────────────────────────── */
export function LogoEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Image">
        <div className="space-y-1">
          <Label className="text-xs text-gray-500">Image URL</Label>
          <Input value={props.src || ""} onChange={(e) => onUpdate({ src: e.target.value })} placeholder="https://example.com/logo.png" className="h-7 text-xs" />
        </div>
        <EjsBindingInput value={props.ejsBinding} onChange={(v) => onUpdate({ ejsBinding: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Size & Position">
        <PropInput label="Width"  type="number" value={props.width}  onChange={(v) => onUpdate({ width: v })} />
        <PropInput label="Height" type="number" value={props.height} onChange={(v) => onUpdate({ height: v })} />
        <PropAlign label="Align"  value={props.alignment} onChange={(v) => onUpdate({ alignment: v })} />
      </PropGroup>
    </>
  );
}

export function ImageEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Image">
        <div className="space-y-1">
          <Label className="text-xs text-gray-500">Image URL</Label>
          <Input value={props.src || ""} onChange={(e) => onUpdate({ src: e.target.value })} placeholder="https://example.com/image.png" className="h-7 text-xs" />
        </div>
        <EjsBindingInput value={props.ejsBinding} onChange={(v) => onUpdate({ ejsBinding: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Size">
        <PropInput label="Width"  type="number" value={props.width}        onChange={(v) => onUpdate({ width: v })} />
        <PropInput label="Height" type="number" value={props.height}       onChange={(v) => onUpdate({ height: v })} />
        <PropInput label="Radius" type="number" value={props.borderRadius} onChange={(v) => onUpdate({ borderRadius: v })} />
        <PropAlign label="Align"  value={props.alignment} onChange={(v) => onUpdate({ alignment: v })} />
      </PropGroup>
    </>
  );
}

export function SignatureEditor({ props, onUpdate }) {
  return (
    <PropGroup title="Signature">
      <PropInput label="Label"      value={props.label}                    onChange={(v) => onUpdate({ label: v })} />
      <PropInput label="Line W."    type="number" value={props.lineWidth}  onChange={(v) => onUpdate({ lineWidth: v })} />
      <PropColor label="Line Color" value={props.lineColor}                onChange={(v) => onUpdate({ lineColor: v })} />
      <PropColor label="Text Color" value={props.color}                    onChange={(v) => onUpdate({ color: v })} />
      <PropAlign label="Align"      value={props.alignment}                onChange={(v) => onUpdate({ alignment: v })} />
    </PropGroup>
  );
}

export function QrCodeEditor({ props, onUpdate }) {
  return (
    <PropGroup title="QR Code">
      <PropInput label="Data" value={props.data} onChange={(v) => onUpdate({ data: v })} />
      <EjsBindingInput value={props.ejsBinding} onChange={(v) => onUpdate({ ejsBinding: v })} />
      <PropInput label="Size"  type="number" value={props.size}      onChange={(v) => onUpdate({ size: v })} />
      <PropAlign label="Align" value={props.alignment} onChange={(v) => onUpdate({ alignment: v })} />
    </PropGroup>
  );
}

export function BarcodeEditor({ props, onUpdate }) {
  return (
    <PropGroup title="Barcode">
      <PropInput label="Data"  value={props.data} onChange={(v) => onUpdate({ data: v })} />
      <EjsBindingInput value={props.ejsBinding} onChange={(v) => onUpdate({ ejsBinding: v })} />
      <PropInput label="Width"  type="number" value={props.width}  onChange={(v) => onUpdate({ width: v })} />
      <PropInput label="Height" type="number" value={props.height} onChange={(v) => onUpdate({ height: v })} />
      <PropAlign label="Align"  value={props.alignment} onChange={(v) => onUpdate({ alignment: v })} />
    </PropGroup>
  );
}

/* ─────────────────────────── INFO ELEMENTS ─────────────────────────── */
export function InvoiceDetailsEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Fields">
        <FieldsEditor fields={props.fields || []} onChange={(v) => onUpdate({ fields: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size"   type="number" value={props.fontSize}   onChange={(v) => onUpdate({ fontSize: v })} />
        <PropColor label="Label"       value={props.labelColor}               onChange={(v) => onUpdate({ labelColor: v })} />
        <PropColor label="Value"       value={props.valueColor}               onChange={(v) => onUpdate({ valueColor: v })} />
        <PropInput label="Label W."    type="number" value={props.labelWidth} onChange={(v) => onUpdate({ labelWidth: v })} />
        <PropAlign label="Label Align" value={props.labelAlign || "left"}     onChange={(v) => onUpdate({ labelAlign: v })} />
        <PropAlign label="Value Align" value={props.valueAlign || "right"}    onChange={(v) => onUpdate({ valueAlign: v })} />
        <PropAlign label="Block Align" value={props.alignment}                onChange={(v) => onUpdate({ alignment: v })} />
      </PropGroup>
    </>
  );
}

export function CustomerInfoEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Header">
        <PropInput label="Title"       value={props.title}      onChange={(v) => onUpdate({ title: v })} />
        <PropColor label="Title Color" value={props.titleColor} onChange={(v) => onUpdate({ titleColor: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Fields">
        <FieldsEditor fields={props.fields || []} onChange={(v) => onUpdate({ fields: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size" type="number" value={props.fontSize} onChange={(v) => onUpdate({ fontSize: v })} />
        <PropColor label="Color"     value={props.color}                  onChange={(v) => onUpdate({ color: v })} />
      </PropGroup>
    </>
  );
}

export function CustomElementEditor({ props, onUpdate }) {
  const fields = props.fields || [];
  const updateField = (i, key, val) => {
    const updated = fields.map((f, idx) => (idx === i ? { ...f, [key]: val } : f));
    onUpdate({ fields: updated });
  };

  return (
    <>
      <PropGroup title="Element">
        <PropInput label="Name" value={props.name} onChange={(v) => onUpdate({ name: v })} />
        <PropSelect
          label="Layout"
          value={props.layout || "vertical"}
          onChange={(v) => onUpdate({ layout: v })}
          options={[
            { value: "vertical",   label: "Vertical" },
            { value: "horizontal", label: "Horizontal" },
            { value: "grid",       label: "Grid (2-col)" },
          ]}
        />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Fields">
        {fields.map((f, i) => (
          <div key={i} className="p-2 border rounded bg-gray-50 space-y-1">
            <span className="text-[10px] text-gray-400">{f.label || f.key || `Field ${i + 1}`}</span>
            <Input
              value={f.defaultValue || ""}
              onChange={(e) => updateField(i, "defaultValue", e.target.value)}
              placeholder="Default value"
              className="h-6 text-xs"
            />
            <Input
              value={f.ejsBinding || ""}
              onChange={(e) => updateField(i, "ejsBinding", e.target.value)}
              placeholder="EJS binding"
              className="h-6 text-xs font-mono"
            />
          </div>
        ))}
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Typography">
        <PropInput  label="Size"        type="number" value={props.fontSize}      onChange={(v) => onUpdate({ fontSize: v })} />
        <PropSelect label="Font"        value={props.fontFamily}                  onChange={(v) => onUpdate({ fontFamily: v })}    options={FONT_OPTIONS} />
        <PropColor  label="Color"       value={props.color}                       onChange={(v) => onUpdate({ color: v })} />
        <PropInput  label="Title Size"  type="number" value={props.titleFontSize} onChange={(v) => onUpdate({ titleFontSize: v })} />
        <PropColor  label="Title Color" value={props.titleColor}                  onChange={(v) => onUpdate({ titleColor: v })} />
      </PropGroup>
    </>
  );
}

/* ─────────────────────────── TABLE ELEMENTS ─────────────────────────── */
const QUICK_COLUMNS = [
  { h: "Tax %",      k: "taxRate",    w: "8%",  a: "right"  },
  { h: "Discount",   k: "discount",   w: "10%", a: "right"  },
  { h: "HSN Code",   k: "hsnCode",    w: "10%", a: "center" },
  { h: "SKU",        k: "sku",        w: "10%", a: "left"   },
  { h: "Net Amount", k: "netAmount",  w: "12%", a: "right"  },
  { h: "Tax Amount", k: "taxAmount",  w: "10%", a: "right"  },
];

export function LineItemsEditor({ props, onUpdate }) {
  const quickAddColumn = (header, key, width, align) => {
    const cols = [...(props.columns || [])];
    if (!cols.some((c) => c.key === key)) {
      cols.push({ header, key, width, align });
      onUpdate({ columns: cols });
    }
  };

  const available = QUICK_COLUMNS.filter(
    ({ k }) => !(props.columns || []).some((c) => c.key === k)
  );

  return (
    <>
      <PropGroup title="Table Columns">
        <p className="text-[10px] text-gray-400 mb-1">Drag handles to reorder columns</p>
        <ColumnsEditor columns={props.columns || []} onChange={(v) => onUpdate({ columns: v })} />
        {available.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 mb-1.5">Quick Add Column:</p>
            <div className="flex flex-wrap gap-1">
              {available.map(({ h, k, w, a }) => (
                <Button key={k} size="sm" variant="outline" className="h-5 text-[9px] px-2" onClick={() => quickAddColumn(h, k, w, a)}>
                  + {h}
                </Button>
              ))}
            </div>
          </div>
        )}
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Column Groups (Nested Headers)">
        <p className="text-[10px] text-gray-400 mb-1">
          Group multiple columns under a shared header (e.g., &quot;GST&quot; spanning Rate + Amount)
        </p>
        <ColumnGroupsEditor groups={props.columnGroups || []} columns={props.columns || []} onChange={(v) => onUpdate({ columnGroups: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Total Row">
        <PropRow label="Show Total">
          <Switch checked={props.showTotalRow || false} onCheckedChange={(v) => onUpdate({ showTotalRow: v })} />
        </PropRow>
        {props.showTotalRow && (
          <PropInput label="Label" value={props.totalLabel ?? ""} onChange={(v) => onUpdate({ totalLabel: v })} />
        )}
        <p className="text-[9px] text-gray-400 mt-1">Enable &quot;Show Total&quot; per column above to sum that column</p>
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Sub-Rows">
        <p className="text-[10px] text-gray-400 mb-1">Extra rows per item (e.g., description below SKU)</p>
        <SubRowsEditor subRows={props.subRows || []} columns={props.columns || []} onChange={(v) => onUpdate({ subRows: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Data">
        <PropInput label="Loop Var"    value={props.loopVariable} onChange={(v) => onUpdate({ loopVariable: v })} />
        <PropInput label="Sample Rows" type="number" value={props.sampleRows} onChange={(v) => onUpdate({ sampleRows: v })} min={1} max={10} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Pagination Overrides (optional)">
        <p className="text-[9px] text-gray-400 mb-1">Leave at 0 to use automatic height-based pagination. Set values to force exact splits.</p>
        <PropInput label="Max Lines/Page" type="number" value={props.maxLinesPerPage || 0} onChange={(v) => onUpdate({ maxLinesPerPage: v })} min={0} max={200} />
        <PropInput label="Wrap At Chars" type="number" value={props.maxCharsPerLine || 0} onChange={(v) => onUpdate({ maxCharsPerLine: v })} min={0} max={300} />
        <PropInput label="Description Key (Wrap Key)" value={props.paginationWrapKey || ""} onChange={(v) => onUpdate({ paginationWrapKey: v })} placeholder="e.g. productGrade" />
        <p className="text-[9px] text-gray-400">Wrap key: the data field whose long text drives extra line counting. Leave empty to disable wrap-aware pagination.</p>
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size"   type="number" value={props.fontSize}        onChange={(v) => onUpdate({ fontSize: v })} />
        <PropColor label="Header BG"   value={props.headerBgColor}                 onChange={(v) => onUpdate({ headerBgColor: v })} />
        <PropColor label="Header Text" value={props.headerTextColor}               onChange={(v) => onUpdate({ headerTextColor: v })} />
        <PropColor label="Border"      value={props.borderColor}                   onChange={(v) => onUpdate({ borderColor: v })} />
        <PropInput label="Cell Pad"    type="number" value={props.cellPadding}     onChange={(v) => onUpdate({ cellPadding: v })} />
        <PropRow   label="All Borders">
          <Switch
            checked={props.allCellBorders || false}
            onCheckedChange={(v) => onUpdate({ allCellBorders: v })}
            data-testid="lineitems-allborders-toggle"
          />
        </PropRow>
        <PropRow   label="Striped">
          <Switch checked={props.stripedRows || false} onCheckedChange={(v) => onUpdate({ stripedRows: v })} />
        </PropRow>
        {props.stripedRows && (
          <PropColor label="Stripe" value={props.stripedColor} onChange={(v) => onUpdate({ stripedColor: v })} />
        )}
      </PropGroup>
    </>
  );
}

export function SummaryTableEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Table Columns">
        <ColumnsEditor columns={props.columns || []} onChange={(v) => onUpdate({ columns: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Column Groups (Nested Headers)">
        <p className="text-[10px] text-gray-400 mb-1">
          Group multiple columns under a shared header (e.g., &quot;GST&quot; spanning CGST + SGST)
        </p>
        <ColumnGroupsEditor groups={props.columnGroups || []} columns={props.columns || []} onChange={(v) => onUpdate({ columnGroups: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Total Row">
        <PropRow label="Show Total">
          <Switch checked={props.showTotalRow || false} onCheckedChange={(v) => onUpdate({ showTotalRow: v })} />
        </PropRow>
        {props.showTotalRow && (
          <PropInput label="Label" value={props.totalLabel ?? ""} onChange={(v) => onUpdate({ totalLabel: v })} />
        )}
        <p className="text-[9px] text-gray-400 mt-1">Enable &quot;Show Total&quot; per column to sum it</p>
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Data">
        <PropInput label="Loop Var"    value={props.loopVariable}        onChange={(v) => onUpdate({ loopVariable: v })} />
        <PropInput label="Sample Rows" type="number" value={props.sampleRows} onChange={(v) => onUpdate({ sampleRows: v })} min={1} max={10} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size"   type="number" value={props.fontSize}    onChange={(v) => onUpdate({ fontSize: v })} />
        <PropColor label="Header BG"   value={props.headerBgColor}             onChange={(v) => onUpdate({ headerBgColor: v })} />
        <PropColor label="Header Text" value={props.headerTextColor}           onChange={(v) => onUpdate({ headerTextColor: v })} />
        <PropColor label="Border"      value={props.borderColor}               onChange={(v) => onUpdate({ borderColor: v })} />
        <PropInput label="Cell Pad"    type="number" value={props.cellPadding} onChange={(v) => onUpdate({ cellPadding: v })} />
        <PropRow   label="All Borders">
          <Switch
            checked={props.allCellBorders || false}
            onCheckedChange={(v) => onUpdate({ allCellBorders: v })}
            data-testid="summary-allborders-toggle"
          />
        </PropRow>
        <PropRow   label="Striped">
          <Switch checked={props.stripedRows || false} onCheckedChange={(v) => onUpdate({ stripedRows: v })} />
        </PropRow>
        {props.stripedRows && (
          <PropColor label="Stripe" value={props.stripedColor} onChange={(v) => onUpdate({ stripedColor: v })} />
        )}
      </PropGroup>
    </>
  );
}

export function TotalsEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Rows">
        <TotalsRowsEditor rows={props.rows || []} onChange={(v) => onUpdate({ rows: v })} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size"   type="number" value={props.fontSize} onChange={(v) => onUpdate({ fontSize: v })} />
        <PropInput label="Width"       type="number" value={props.width}    onChange={(v) => onUpdate({ width: v })} />
        <PropColor label="Label"       value={props.labelColor} onChange={(v) => onUpdate({ labelColor: v })} />
        <PropColor label="Value"       value={props.valueColor} onChange={(v) => onUpdate({ valueColor: v })} />
        <PropAlign label="Label Align" value={props.labelAlign || "left"}  onChange={(v) => onUpdate({ labelAlign: v })} />
        <PropAlign label="Value Align" value={props.valueAlign || "right"} onChange={(v) => onUpdate({ valueAlign: v })} />
        <PropAlign label="Block Align" value={props.alignment} onChange={(v) => onUpdate({ alignment: v })} />
      </PropGroup>
    </>
  );
}

/* ─────────────────────────── LAYOUT ELEMENTS ─────────────────────────── */
function ColumnSlotEditor({ slotKey, label, accentClass, props, onUpdate, typeOptions }) {
  const customElements = useCustomElementsList();

  const changeColumnType = (col, newType) => {
    // Handle "custom:<id>" pseudo-types
    if (newType.startsWith("custom:")) {
      const customId = newType.slice("custom:".length);
      const def = customElements.find((c) => c.id === customId);
      if (!def) return;
      const fields = JSON.parse(JSON.stringify(def.fields || []));
      onUpdate({
        [col]: {
          type: "custom",
          props: {
            customElementId: def.id,
            name: def.name,
            fields,
            layout: def.style?.layout || "vertical",
            fontSize: def.style?.fontSize || 12,
            fontFamily: def.style?.fontFamily || "Arial, sans-serif",
            color: def.style?.color || "#374151",
            titleFontSize: def.style?.titleFontSize || 13,
            titleFontWeight: def.style?.titleFontWeight || "bold",
            titleColor: def.style?.titleColor || "#111827",
            marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0,
          },
        },
      });
      return;
    }
    const defaults = JSON.parse(JSON.stringify(ELEMENT_TYPES[newType].defaultProps));
    onUpdate({ [col]: { type: newType, props: defaults } });
  };
  const updateColumnProps = (col, newProps) => {
    onUpdate({ [col]: { ...props[col], props: { ...props[col]?.props, ...newProps } } });
  };
  const updateColumnElementBorder = (col, nextBorder) => {
    onUpdate({ [col]: { ...props[col], elementBorder: nextBorder } });
  };
  const slot = props[slotKey];

  // Compute the "current value" string for the Select trigger.
  // For custom slots, identify by customElementId.
  const currentValue = slot?.type === "custom" && slot?.props?.customElementId
    ? `custom:${slot.props.customElementId}`
    : slot?.type;

  // Build option list: standard types + each saved custom element.
  const allOptions = [
    ...typeOptions,
    ...(customElements.length > 0 ? customElements.map((c) => ({ value: `custom:${c.id}`, label: `★ ${c.name}` })) : []),
  ];

  return (
    <PropGroup title={label}>
      <PropSelect label="Element" value={currentValue} onChange={(v) => changeColumnType(slotKey, v)} options={allOptions} />
      {slot?.type && (
        <div className={`mt-2 pl-2 border-l-2 ${accentClass}`}>
          <TypeEditor element={{ type: slot.type, props: slot.props || {} }} onUpdate={(p) => updateColumnProps(slotKey, p)} />
        </div>
      )}
      {/* Per-slot inner border — independent of the layout's global wrapper border.
          Persists on the slot itself (props[slotKey].elementBorder) so each
          column has its own four-sided border configuration. */}
      <SlotInnerBorderEditor
        value={slot?.elementBorder}
        onChange={(next) => updateColumnElementBorder(slotKey, next)}
        testIdPrefix={`slot-elborder-${slotKey}`}
      />
    </PropGroup>
  );
}

/* ── Small helper: compute usable canvas width in mm to warn on narrow columns ── */
function useUsableWidthMM() {
  const ps = usePageSettings();
  if (!ps || !ps.pageSize) return null;
  const size = getPageSize(ps.pageSize, ps);
  const w = size.widthMM - (ps.marginLeft || 20) - (ps.marginRight || 20);
  return Math.max(0, Math.round(w));
}

function NarrowWidthWarning({ slotCount }) {
  const usable = useUsableWidthMM();
  if (!usable) return null;
  const perCol = Math.floor(usable / slotCount);
  if (perCol >= 50) return null;
  return (
    <p
      className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2"
      data-testid="width-warning"
    >
      ⚠ At {slotCount} columns, each column gets ~{perCol} mm. Less than 50 mm may cause
      content to overflow. Consider a larger page size or fewer columns.
    </p>
  );
}

export function TwoColumnEditor({ props, onUpdate }) {
  const typeOptions = COLUMN_ELEMENT_TYPES.map((t) => ({
    value: t,
    label: ELEMENT_TYPES[t].label,
  }));
  return (
    <>
      <NarrowWidthWarning slotCount={2} />
      <PropGroup title="Layout">
        <PropInput label="Left W." value={props.leftWidth} onChange={(v) => onUpdate({ leftWidth: v })} />
        <PropInput label="Gap"     type="number" value={props.gap} onChange={(v) => onUpdate({ gap: v })} min={0} max={60} />
        <PropSelect
          label="V. Align"
          value={props.verticalAlign}
          onChange={(v) => onUpdate({ verticalAlign: v })}
          options={[{ value: "top", label: "Top" }, { value: "center", label: "Center" }, { value: "bottom", label: "Bottom" }]}
        />
      </PropGroup>
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="leftColumn"  label="Left Column"  accentClass="border-blue-200" props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="rightColumn" label="Right Column" accentClass="border-blue-200" props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
    </>
  );
}

export function ThreeColumnEditor({ props, onUpdate }) {
  const typeOptions = COLUMN_ELEMENT_TYPES
    .filter((t) => t !== "threeColumn" && t !== "twoColumn" && t !== "fourColumn")
    .map((t) => ({ value: t, label: ELEMENT_TYPES[t].label }));
  return (
    <>
      <NarrowWidthWarning slotCount={3} />
      <PropGroup title="Layout">
        <PropInput label="Left W." value={props.leftWidth} onChange={(v) => onUpdate({ leftWidth: v })} />
        <PropInput label="Mid W."  value={props.midWidth}  onChange={(v) => onUpdate({ midWidth: v })} />
        <PropInput label="Gap"     type="number" value={props.gap} onChange={(v) => onUpdate({ gap: v })} min={0} max={60} />
        <PropSelect
          label="V. Align"
          value={props.verticalAlign}
          onChange={(v) => onUpdate({ verticalAlign: v })}
          options={[{ value: "top", label: "Top" }, { value: "center", label: "Center" }, { value: "bottom", label: "Bottom" }]}
        />
      </PropGroup>
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="leftColumn"  label="Left Column"   accentClass="border-blue-200"   props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="midColumn"   label="Middle Column" accentClass="border-green-200"  props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="rightColumn" label="Right Column"  accentClass="border-purple-200" props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
    </>
  );
}

export function FourColumnEditor({ props, onUpdate }) {
  const typeOptions = COLUMN_ELEMENT_TYPES
    .filter((t) => t !== "threeColumn" && t !== "twoColumn" && t !== "fourColumn")
    .map((t) => ({ value: t, label: ELEMENT_TYPES[t].label }));
  return (
    <>
      <NarrowWidthWarning slotCount={4} />
      <PropGroup title="Layout">
        <PropInput label="Col1 W." value={props.col1Width} onChange={(v) => onUpdate({ col1Width: v })} />
        <PropInput label="Col2 W." value={props.col2Width} onChange={(v) => onUpdate({ col2Width: v })} />
        <PropInput label="Col3 W." value={props.col3Width} onChange={(v) => onUpdate({ col3Width: v })} />
        <PropInput label="Col4 W." value={props.col4Width} onChange={(v) => onUpdate({ col4Width: v })} />
        <PropInput label="Gap"     type="number" value={props.gap} onChange={(v) => onUpdate({ gap: v })} min={0} max={60} />
        <PropSelect
          label="V. Align"
          value={props.verticalAlign}
          onChange={(v) => onUpdate({ verticalAlign: v })}
          options={[{ value: "top", label: "Top" }, { value: "center", label: "Center" }, { value: "bottom", label: "Bottom" }]}
        />
        <p className="text-[9px] text-gray-400">All four widths can be set explicitly (recommended sum: 100%).</p>
      </PropGroup>
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="col1" label="Column 1" accentClass="border-blue-200"   props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="col2" label="Column 2" accentClass="border-green-200"  props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="col3" label="Column 3" accentClass="border-purple-200" props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
      <Separator className="my-3" />
      <ColumnSlotEditor slotKey="col4" label="Column 4" accentClass="border-amber-200"  props={props} onUpdate={onUpdate} typeOptions={typeOptions} />
    </>
  );
}

export function DividerEditor({ props, onUpdate }) {
  return (
    <PropGroup title="Divider">
      <PropColor  label="Color"     value={props.color}     onChange={(v) => onUpdate({ color: v })} />
      <PropInput  label="Thickness" type="number" value={props.thickness} onChange={(v) => onUpdate({ thickness: v })} min={1} max={10} />
      <PropSelect label="Style"     value={props.style}     onChange={(v) => onUpdate({ style: v })}     options={["solid", "dashed", "dotted"]} />
    </PropGroup>
  );
}

/**
 * Vertical Layout — recursive container. Holds N stacked rows; each row's
 * `childType`/`childProps` independently host any other element type. Rows
 * can be added/removed and the inner element type swapped at any time.
 *
 * Excluded from the type dropdown:
 *  - verticalLayout itself, to prevent infinite recursion via the UI
 *  - lineItems / summaryTable, which assume full-page pagination context
 */
export function VerticalLayoutEditor({ props, onUpdate }) {
  const typeOptions = COLUMN_ELEMENT_TYPES
    .filter((t) => t !== "verticalLayout" && t !== "lineItems" && t !== "summaryTable")
    .map((t) => ({ value: t, label: ELEMENT_TYPES[t].label }));

  const rows = props.rows || [];

  const updateRows = (next) => onUpdate({ rows: next });
  const addRow = () => updateRows([
    ...rows,
    { id: `vrow-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, childType: null, childProps: {} },
  ]);
  const removeRow = (i) => updateRows(rows.filter((_, idx) => idx !== i));
  const updateChild = (i, patch) => updateRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const changeChildType = (i, newType) => {
    // The PropSelect uses a sentinel "__empty__" value because Radix Select
    // forbids empty-string item values. Translate back to null here.
    if (!newType || newType === "__empty__") {
      updateChild(i, { childType: null, childProps: {} });
      return;
    }
    const defaults = JSON.parse(JSON.stringify(ELEMENT_TYPES[newType]?.defaultProps || {}));
    updateChild(i, { childType: newType, childProps: defaults });
  };

  return (
    <>
      <PropGroup title="Layout">
        <PropInput label="Gap" type="number" value={props.gap} onChange={(v) => onUpdate({ gap: v })} min={0} max={60} />
        <p className="text-[9px] text-gray-400">Rows render full-width and stack vertically. Each row holds one child element.</p>
      </PropGroup>
      <Separator className="my-3" />

      <PropGroup title="Stacked Rows">
        {rows.length === 0 ? (
          <p className="text-[10px] text-gray-400 italic px-1">No rows added yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={r.id || i} className="p-2 border rounded-md bg-gray-50" data-testid={`vrow-${i}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[10px] text-gray-400 font-medium">Row {i + 1}</span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-5 w-5 text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid={`vrow-delete-${i}`}
                    onClick={() => removeRow(i)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <PropSelect
                  label="Element"
                  value={r.childType || "__empty__"}
                  onChange={(v) => changeChildType(i, v)}
                  options={[{ value: "__empty__", label: "— Empty —" }, ...typeOptions]}
                />
                {r.childType && ELEMENT_TYPES[r.childType] && (
                  <div className="mt-2 pl-2 border-l-2 border-emerald-200">
                    <TypeEditor
                      element={{ type: r.childType, props: r.childProps || {} }}
                      onUpdate={(p) => updateChild(i, { childProps: { ...r.childProps, ...p } })}
                    />
                  </div>
                )}
                {/* Per-row inner border — independent of the vertical layout's
                    global wrapper border. Persists on rows[i].elementBorder so
                    each stacked row carries its own four-sided border config. */}
                <SlotInnerBorderEditor
                  value={r.elementBorder}
                  onChange={(next) => updateChild(i, { elementBorder: next })}
                  testIdPrefix={`vrow-elborder-${i}`}
                />
              </div>
            ))}
          </div>
        )}
        <Button size="sm" variant="outline" className="w-full h-7 text-xs mt-2" onClick={addRow} data-testid="vrow-add">
          + Add Row
        </Button>
      </PropGroup>
    </>
  );
}

export function SpacerEditor({ props, onUpdate }) {
  return (
    <PropGroup title="Spacer">
      <PropInput label="Height" type="number" value={props.height} onChange={(v) => onUpdate({ height: v })} min={4} max={200} />
    </PropGroup>
  );
}

export function PageBreakEditor() {
  return (
    <PropGroup title="Page Break">
      <p className="text-[10px] text-gray-400">
        Forces a new page in the printed/PDF output. No additional properties needed.
      </p>
    </PropGroup>
  );
}

/* ─────────────────────────── DYNAMIC ELEMENTS ─────────────────────────── */
export function PageNumberEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Format">
        <PropInput label="Format" value={props.format} onChange={(v) => onUpdate({ format: v })} />
        <p className="text-[9px] text-gray-400">
          Use {"{current}"} and {"{total}"} as placeholders
        </p>
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput  label="Font Size" type="number" value={props.fontSize} onChange={(v) => onUpdate({ fontSize: v })} />
        <PropSelect label="Font"      value={props.fontFamily}             onChange={(v) => onUpdate({ fontFamily: v })} options={FONT_OPTIONS} />
        <PropAlign  label="Align"     value={props.textAlign}              onChange={(v) => onUpdate({ textAlign: v })} />
        <PropColor  label="Color"     value={props.color}                  onChange={(v) => onUpdate({ color: v })} />
      </PropGroup>
    </>
  );
}

export function CarriedForwardEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Carried Forward">
        <PropInput label="Label"      value={props.label}     onChange={(v) => onUpdate({ label: v })} />
        <PropInput label="Amount Key" value={props.amountKey} onChange={(v) => onUpdate({ amountKey: v })} />
        <p className="text-[9px] text-gray-400">
          The data key from line items to sum (e.g. &quot;amount&quot;)
        </p>
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput  label="Font Size" type="number" value={props.fontSize} onChange={(v) => onUpdate({ fontSize: v })} />
        <PropSelect label="Font"      value={props.fontFamily}             onChange={(v) => onUpdate({ fontFamily: v })} options={FONT_OPTIONS} />
        <PropSelect label="Weight"    value={props.fontWeight}             onChange={(v) => onUpdate({ fontWeight: v })} options={["normal", "bold"]} />
        <PropAlign  label="Align"     value={props.textAlign}              onChange={(v) => onUpdate({ textAlign: v })} />
        <PropColor  label="Color"     value={props.color}                  onChange={(v) => onUpdate({ color: v })} />
      </PropGroup>
    </>
  );
}

export function BroughtForwardEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Brought Forward">
        <PropInput label="Label"      value={props.label}     onChange={(v) => onUpdate({ label: v })} />
        <PropInput label="Amount Key" value={props.amountKey} onChange={(v) => onUpdate({ amountKey: v })} />
        <p className="text-[9px] text-gray-400">
          The data key from line items to sum (e.g. &quot;amount&quot;)
        </p>
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput  label="Font Size" type="number" value={props.fontSize} onChange={(v) => onUpdate({ fontSize: v })} />
        <PropSelect label="Font"      value={props.fontFamily}             onChange={(v) => onUpdate({ fontFamily: v })} options={FONT_OPTIONS} />
        <PropSelect label="Weight"    value={props.fontWeight}             onChange={(v) => onUpdate({ fontWeight: v })} options={["normal", "bold"]} />
        <PropAlign  label="Align"     value={props.textAlign}              onChange={(v) => onUpdate({ textAlign: v })} />
        <PropColor  label="Color"     value={props.color}                  onChange={(v) => onUpdate({ color: v })} />
      </PropGroup>
    </>
  );
}


/* ─────────────────────────── KEY-VALUE LIST & TAX DETAILS ─────────────────────────── */
/**
 * Shared row editor used by Tax Details (preLoopItems / postLoopItems) and
 * Key-Value List (items). Every row — including the first — has a working
 * delete button (Trash icon). The list can be reduced to zero rows.
 *
 * Props:
 *   - withBold:    show a per-row Bold toggle (tax details)
 *   - withRowType: show per-row "Label & Value" vs "Value Only" toggle (KV list)
 *   - withDual:    show a "+ Add Dual Row" button (KV list only)
 *
 * Schema additions for KV list rows:
 *   - layoutPattern: "single" (default) | "dual" — dual mode renders a
 *     side-by-side two-pair row using left/right sub-objects.
 *   - defaultValue: static fallback text shown when ejsBinding is empty.
 *   - rowFontSize: per-row font size override (string/number, "" = inherit).
 *   - rowIsBold:   per-row bold toggle (boolean).
 */
function KvRowsEditor({ items, onChange, withBold = false, withRowType = false, withDual = false }) {
  const newRow = () => ({
    id: `kv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    layoutPattern: "single",
    label: "Label",
    ejsBinding: "",
    defaultValue: "",
    rowFontSize: "",
    rowIsBold: false,
    ...(withBold ? { isBold: false } : {}),
    ...(withRowType ? { rowType: "label_value", staticText: "" } : {}),
  });
  const newDualRow = () => ({
    id: `kv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    layoutPattern: "dual",
    left:  { label: "Label", ejsBinding: "", defaultValue: "" },
    right: { label: "Label", ejsBinding: "", defaultValue: "" },
  });
  const add = () => onChange([...items, newRow()]);
  const addDual = () => onChange([...items, newDualRow()]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, key, val) => onChange(items.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const updateSide = (i, side, key, val) => onChange(
    items.map((r, idx) => (idx === i ? { ...r, [side]: { ...(r[side] || {}), [key]: val } } : r))
  );

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <p className="text-[10px] text-gray-400 italic px-1">No rows added yet.</p>
      ) : (
        items.map((r, i) => {
          const layoutPattern = r.layoutPattern || "single";
          const rowType = r.rowType || "label_value";
          const isValueOnly = withRowType && rowType === "value_only";

          // DUAL ROW LAYOUT — independent left + right pairs.
          if (layoutPattern === "dual") {
            return (
              <div key={r.id || i} className="p-2 border-2 border-blue-200 rounded-md bg-blue-50/30 space-y-2" data-testid={`kv-row-${i}`}>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-blue-600 font-bold uppercase tracking-wide">Dual Row {i + 1}</span>
                  <Button size="icon" variant="ghost" className="h-5 w-5 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`kv-row-delete-${i}`} onClick={() => remove(i)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {/* Left pair */}
                  <div className="space-y-1 p-2 bg-white rounded border border-gray-200">
                    <span className="text-[9px] text-gray-400 font-medium uppercase">Left Pair</span>
                    <Input value={r.left?.label ?? ""}        onChange={(e) => updateSide(i, "left", "label", e.target.value)}        placeholder="Label" className="h-6 text-xs" data-testid={`kv-row-${i}-left-label`} />
                    <Input value={r.left?.ejsBinding ?? ""}   onChange={(e) => updateSide(i, "left", "ejsBinding", e.target.value)}   placeholder="EJS path" className="h-6 text-xs font-mono" data-testid={`kv-row-${i}-left-binding`} />
                    <Input value={r.left?.defaultValue ?? ""} onChange={(e) => updateSide(i, "left", "defaultValue", e.target.value)} placeholder="Default value" className="h-6 text-xs" data-testid={`kv-row-${i}-left-default`} />
                  </div>
                  {/* Right pair */}
                  <div className="space-y-1 p-2 bg-white rounded border border-gray-200">
                    <span className="text-[9px] text-gray-400 font-medium uppercase">Right Pair</span>
                    <Input value={r.right?.label ?? ""}        onChange={(e) => updateSide(i, "right", "label", e.target.value)}        placeholder="Label" className="h-6 text-xs" data-testid={`kv-row-${i}-right-label`} />
                    <Input value={r.right?.ejsBinding ?? ""}   onChange={(e) => updateSide(i, "right", "ejsBinding", e.target.value)}   placeholder="EJS path" className="h-6 text-xs font-mono" data-testid={`kv-row-${i}-right-binding`} />
                    <Input value={r.right?.defaultValue ?? ""} onChange={(e) => updateSide(i, "right", "defaultValue", e.target.value)} placeholder="Default value" className="h-6 text-xs" data-testid={`kv-row-${i}-right-default`} />
                  </div>
                </div>
              </div>
            );
          }

          // SINGLE ROW LAYOUT — standard label/value with optional Value-Only variant.
          return (
            <div key={r.id || i} className="p-2 border rounded-md bg-gray-50 space-y-1" data-testid={`kv-row-${i}`}>
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-gray-400">Row {i + 1}</span>
                <Button size="icon" variant="ghost" className="h-5 w-5 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`kv-row-delete-${i}`} onClick={() => remove(i)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>

              {withRowType && (
                <div className="flex gap-0.5 mb-1">
                  <Button size="sm" variant={rowType === "label_value" ? "default" : "outline"} className="h-6 text-[10px] flex-1 px-1" data-testid={`kv-row-${i}-type-label-value`} onClick={() => update(i, "rowType", "label_value")}>Label & Value</Button>
                  <Button size="sm" variant={rowType === "value_only" ? "default" : "outline"} className="h-6 text-[10px] flex-1 px-1" data-testid={`kv-row-${i}-type-value-only`} onClick={() => update(i, "rowType", "value_only")}>Value Only</Button>
                </div>
              )}

              {!isValueOnly && (
                <Input value={r.label ?? ""} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label" className="h-6 text-xs" data-testid={`kv-row-${i}-label`} />
              )}
              <Input value={r.ejsBinding ?? ""} onChange={(e) => update(i, "ejsBinding", e.target.value)} placeholder="Dynamic Key / EJS binding (e.g. invoice.number)" className="h-6 text-xs font-mono" data-testid={`kv-row-${i}-binding`} />

              {/* "Static Text / Default Value" — used for Value-Only and as fallback for Label & Value */}
              {isValueOnly ? (
                <Input value={r.staticText ?? ""} onChange={(e) => update(i, "staticText", e.target.value)} placeholder="Static Text / Default Value" className="h-6 text-xs" data-testid={`kv-row-${i}-static`} />
              ) : (
                withRowType && (
                  <Input value={r.defaultValue ?? ""} onChange={(e) => update(i, "defaultValue", e.target.value)} placeholder="Static Text / Default Value" className="h-6 text-xs" data-testid={`kv-row-${i}-default`} />
                )
              )}

              {/* Per-row typography micro-controls — apply when this row needs to break the global style. */}
              {isValueOnly && (
                <div className="flex items-end gap-1 mt-1">
                  <div className="flex flex-col gap-0.5 flex-1">
                    <Label className="text-[9px] text-gray-400 uppercase">Size</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="inherit"
                      value={r.rowFontSize ?? ""}
                      onChange={(e) => update(i, "rowFontSize", e.target.value)}
                      className="h-6 text-xs"
                      data-testid={`kv-row-${i}-fontsize`}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant={r.rowIsBold ? "default" : "outline"}
                    className="h-6 w-6 text-xs font-bold p-0"
                    data-testid={`kv-row-${i}-bold`}
                    onClick={() => update(i, "rowIsBold", !r.rowIsBold)}
                    title="Bold"
                  >B</Button>
                </div>
              )}

              {withBold && (
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] text-gray-400">Bold</Label>
                  <Switch checked={r.isBold || false} onCheckedChange={(v) => update(i, "isBold", v)} className="scale-75" />
                </div>
              )}
            </div>
          );
        })
      )}
      <div className="flex gap-1">
        <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={add} data-testid="kv-row-add">+ Add Row</Button>
        {withDual && (
          <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={addDual} data-testid="kv-row-add-dual">+ Add Dual Row</Button>
        )}
      </div>
    </div>
  );
}

export function KeyValueListEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Section Heading (optional)">
        <PropInput
          label="Heading"
          value={props.componentHeading ?? ""}
          onChange={(v) => onUpdate({ componentHeading: v })}
          placeholder="e.g. Recipient Details / Bill To"
        />
        <p className="text-[9px] text-gray-400">Compiled as static plain text — no EJS evaluation.</p>
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Rows">
        <KvRowsEditor items={props.items || []} onChange={(v) => onUpdate({ items: v })} withRowType withDual />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size"   type="number" value={props.fontSize}   onChange={(v) => onUpdate({ fontSize: v })} />
        <PropSelect label="Font"       value={props.fontFamily}               onChange={(v) => onUpdate({ fontFamily: v })} options={FONT_OPTIONS} />
        <PropColor label="Label"       value={props.labelColor} onChange={(v) => onUpdate({ labelColor: v })} />
        <PropColor label="Value"       value={props.valueColor} onChange={(v) => onUpdate({ valueColor: v })} />
        <PropInput label="Label W."    value={props.labelWidth} onChange={(v) => onUpdate({ labelWidth: v })} />
        <PropAlign label="Label Align" value={props.labelAlign || "left"}  onChange={(v) => onUpdate({ labelAlign: v })} />
        <PropAlign label="Value Align" value={props.valueAlign || "left"}  onChange={(v) => onUpdate({ valueAlign: v })} />
        <PropInput label="Gap"         type="number" value={props.gap} onChange={(v) => onUpdate({ gap: v })} min={0} max={40} />
        <PropInput label="Vertical Gap" type="number" value={props.verticalGap ?? 4} onChange={(v) => onUpdate({ verticalGap: v })} min={0} max={40} />
      </PropGroup>
    </>
  );
}

export function TaxDetailsEditor({ props, onUpdate }) {
  return (
    <>
      <PropGroup title="Pre-Loop Rows">
        <p className="text-[10px] text-gray-400 mb-1">Rendered once, before the per-row breakdown (e.g. &quot;Sub Total&quot;).</p>
        <KvRowsEditor items={props.preLoopItems || []} onChange={(v) => onUpdate({ preLoopItems: v })} withBold />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Loop (per-row)">
        <PropInput label="Loop Var"  value={props.loopVariable}      onChange={(v) => onUpdate({ loopVariable: v })} />
        <PropInput label="Label Key" value={props.loopRowLabelKey}   onChange={(v) => onUpdate({ loopRowLabelKey: v })} />
        <PropInput label="Value Key" value={props.loopRowValueKey}   onChange={(v) => onUpdate({ loopRowValueKey: v })} />
        <PropInput label="Sample N"  type="number" value={props.sampleRows} onChange={(v) => onUpdate({ sampleRows: v })} min={0} max={20} />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Post-Loop Rows">
        <p className="text-[10px] text-gray-400 mb-1">Rendered once, after the per-row breakdown (e.g. &quot;Grand Total&quot;).</p>
        <KvRowsEditor items={props.postLoopItems || []} onChange={(v) => onUpdate({ postLoopItems: v })} withBold />
      </PropGroup>
      <Separator className="my-3" />
      <PropGroup title="Style">
        <PropInput label="Font Size"   type="number" value={props.fontSize}   onChange={(v) => onUpdate({ fontSize: v })} />
        <PropSelect label="Font"       value={props.fontFamily}               onChange={(v) => onUpdate({ fontFamily: v })} options={FONT_OPTIONS} />
        <PropColor label="Label"       value={props.labelColor} onChange={(v) => onUpdate({ labelColor: v })} />
        <PropColor label="Value"       value={props.valueColor} onChange={(v) => onUpdate({ valueColor: v })} />
        <PropInput label="Label W."    value={props.labelWidth} onChange={(v) => onUpdate({ labelWidth: v })} />
        <PropAlign label="Label Align" value={props.labelAlign || "left"}   onChange={(v) => onUpdate({ labelAlign: v })} />
        <PropAlign label="Value Align" value={props.valueAlign || "right"}  onChange={(v) => onUpdate({ valueAlign: v })} />
        <PropInput label="Row Pad"     type="number" value={props.rowPadding}            onChange={(v) => onUpdate({ rowPadding: v })} min={0} max={20} />
        <PropColor label="Divider"     value={props.sectionDividerColor}                 onChange={(v) => onUpdate({ sectionDividerColor: v })} />
        <PropInput label="Divider W"   type="number" value={props.sectionDividerWidth}   onChange={(v) => onUpdate({ sectionDividerWidth: v })} min={0} max={8} />
      </PropGroup>
    </>
  );
}


/* ─── Recursive TypeEditor (used by ColumnSlotEditor and VerticalLayoutEditor) ──
   Imported via the registry proxy to break the import cycle with TypeEditor.jsx.
   The real component registers itself on load; the proxy resolves at render time. */
import { TypeEditor } from "./typeEditorRegistry";
